import {
  computationTableSx,
  computationTableContainerSx,
} from "./computationTableStyles";
import { formatAmount } from "../../../../utils/hooks/dataHandler";
import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import { JustInfo } from "../../../utils/Icon";
import CustomDatePicker from "../../../utils/DatePicker/CustomDatePicker";
import { format, parseISO } from "date-fns";
import { startupEligibilityLabel, yesNoLabel } from "../domain/review";
import { calculateGroupReliefOutput } from "../domain/groupReliefComputation";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";
import {
  exportGroupReliefExcel,
  exportGroupReliefPdf,
} from "../importExport/groupReliefExport";

const fields = [
  ["caTransferred", "CA"],
  ["lossesTransferred", "Trade losses"],
  ["donationsTransferred", "Donations"],
];
const preparerFields = ["fullName", "capacity", "contactNumber", "date"];

const numberValue = (value) => {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? numeric : 0;
};

const buildDetailedOutputRows = (result, options = {}) => {
  const { showTransferRows = true } = options;
  const companyRows = result.companies;
  const postRows = result.postComputations;
  const inputs = companyRows.map(
    (company) => company.resolution?.groupRelief?.detailedOutput || {}
  );
  const inputValues = (key) => inputs.map((input) => numberValue(input[key]));
  const calculated = companyRows.map((company, index) =>
    calculateGroupReliefOutput(
      inputs[index],
      result.adjustments?.[company.id] || {}
    )
  );
  const calculatedValues = (key) => calculated.map((item) => item[key]);
  const postValues = (key) => postRows.map((item) => numberValue(item[key]));
  const transferRows = (category, label) => {
    const totals = new Map();
    result.transfers.forEach((transfer) => {
      const amount = numberValue(transfer[category]);
      if (amount <= 0) return;
      totals.set(
        transfer.transferorId,
        (totals.get(transfer.transferorId) || 0) + amount
      );
      totals.set(
        transfer.claimantId,
        (totals.get(transfer.claimantId) || 0) - amount
      );
    });
    return [
      {
        id: `${category}-total`,
        label,
        className: "transfer-step-row",
        values: companyRows.map((company) => totals.get(company.id) || 0),
      },
    ];
  };
  const maybeTransferRows = (category, label) =>
    showTransferRows ? transferRows(category, label) : [];
  const spacer = (id) => ({
    id,
    label: "",
    values: companyRows.map(() => ""),
    numeric: false,
    className: "spacer-row",
  });

  return [
    {
      id: "company-name",
      label: "Company Name",
      values: companyRows.map((company) => company.name),
      numeric: false,
    },
    {
      id: "uen",
      label: "UEN",
      values: companyRows.map((company) => company.uen),
      numeric: false,
    },
    {
      id: "sute",
      label: "Qualifies for SUTE",
      values: companyRows.map((company) =>
        startupEligibilityLabel(
          company.isStartup,
          company.taxPeriodType,
          result.settings.yearOfAssessment
        )
      ),
      numeric: false,
    },
    {
      id: "shareholding-maintained",
      label: "75% Maintained Throughout",
      values: companyRows.map((company) =>
        yesNoLabel(company.shareholdingMaintainedThroughout)
      ),
      numeric: false,
    },
    {
      id: "condition-final-day",
      label: "75% Met on Last Day",
      values: companyRows.map((company) =>
        yesNoLabel(company.conditionMetOnFinalDay)
      ),
      numeric: false,
    },
    {
      id: "qualifying-days",
      label: "Continuous Qualifying Days",
      values: companyRows.map((company) => company.continuousQualifyingDays),
    },
    {
      id: "adjusted-profit",
      label: "Adjusted Profit",
      values: inputValues("adjustedProfit"),
    },
    {
      id: "prior-ca",
      label: "Prior-Year Unutilised CA",
      className: "input-balance-row",
      values: inputValues("priorYearCA"),
    },
    {
      id: "current-ca",
      label: "Current-Year CA",
      className: "input-balance-row",
      values: inputValues("currentYearCA"),
    },
    ...maybeTransferRows("caTransferred", "Group relief transfer CA"),
    {
      id: "after-ca",
      label: "Adjusted Profit after CA",
      isSubtotal: true,
      values: calculatedValues("adjustedProfitAfterCA"),
    },
    {
      id: "other-income",
      label: "Other-Sourced Income",
      className: "input-balance-row",
      values: inputValues("otherSourcedIncome"),
    },
    {
      id: "statutory-income",
      label: "Statutory income",
      isSubtotal: true,
      values: calculatedValues("statutoryIncome"),
    },
    {
      id: "prior-losses",
      label: "Prior-Year Unutilised Losses",
      values: inputValues("priorYearLosses"),
    },
    {
      id: "current-losses",
      label: "Current-Year Losses",
      className: "input-balance-row",
      values: inputValues("currentYearLosses"),
    },
    ...maybeTransferRows("lossesTransferred", "Group relief transfer losses"),
    {
      id: "assessable-income",
      label: "Statutory income less losses",
      isSubtotal: true,
      values: calculatedValues("statutoryIncomeLessLosses"),
    },
    {
      id: "prior-donations",
      label: "Prior-Year Unutilised Donations",
      className: "input-balance-row",
      values: inputValues("priorYearDonations"),
    },
    {
      id: "current-donations",
      label: "Current-Year Donations",
      values: inputValues("currentYearDonations"),
    },
    ...maybeTransferRows(
      "donationsTransferred",
      "Group relief transfer donations"
    ),
    {
      id: "chargeable-income",
      label: "Assessable / Chargeable income",
      isSubtotal: true,
      values: calculatedValues("assessableIncome"),
    },
    spacer("spacer-1"),
    {
      id: "tax-exemption",
      label: "Tax exemption",
      values: postValues("taxExemption").map((value) => -value),
    },
    {
      id: "net-chargeable-income",
      label: "Net chargeable income",
      isSubtotal: true,
      className: "subtotal-row",
      values: postRows.map((item) =>
        Math.max(
          0,
          numberValue(item.chargeableIncome) - numberValue(item.taxExemption)
        )
      ),
    },
    spacer("spacer-2"),
    {
      id: "tax-payable-before-rebate",
      label: "Tax payable at 17%",
      values: postValues("taxBeforeRebate"),
      decimals: 2,
    },
    spacer("spacer-3"),
    {
      id: "cit-rebate",
      label: "CIT rebate",
      values: postValues("citRebate").map((value) => -value),
      decimals: 2,
    },
    spacer("spacer-4"),
    {
      id: "tax-payable-before-eci",
      label: "Tax payable before ECI",
      values: postValues("taxPayableBeforeEci"),
      decimals: 2,
    },
  ];
};

const formatDetailedOutputCell = (value, row) => {
  if (row.className === "spacer-row") return "";
  if (row.numeric === false) return value || "-";
  const numeric = numberValue(value);
  const formatted = formatAmount(
    numeric,
    !row.decimals,
    false,
    false,
    Boolean(row.decimals)
  );
  return numeric === 0 ? "-" : formatted;
};

const ResultCompanyLabel = ({ company, justifyContent = "flex-start" }) => (
  <Stack
    component='span'
    direction='row'
    alignItems='center'
    justifyContent={justifyContent}
    gap={0.5}
    sx={{ minWidth: 0 }}
  >
    <Typography component='span' variant='body2' fontWeight={700} noWrap>
      {company?.name || "—"}
    </Typography>
    {company?.isExternal && (
      <Chip
        component='span'
        label='EXTERNAL'
        size='small'
        color='secondary'
        variant='outlined'
        sx={{ height: 18, fontSize: "0.6rem", fontWeight: 750, flexShrink: 0 }}
      />
    )}
  </Stack>
);

export default function ResultsStep({ workspace: w }) {
  const [declarations, setDeclarations] = useState({});
  const [preparerShareNotice, setPreparerShareNotice] = useState("");
  const [outputExporting, setOutputExporting] = useState("");
  const [outputExportError, setOutputExportError] = useState("");
  const r = w.result;
  if (!r) return null;
  const companyById = new Map(
    r.companies.map((company) => [company.id, company])
  );
  const parties = r.companies.filter((company) =>
    r.transfers.some(
      (t) => t.transferorId === company.id || t.claimantId === company.id
    )
  );
  const transferors = parties.filter((company) =>
    r.transfers.some((transfer) => transfer.transferorId === company.id)
  );
  const allTransferorDeclarationsConfirmed = transferors.every((company) =>
    Boolean(declarations[company.id]?.transferorDeclarationsConfirmed)
  );
  const firstParty = parties[0];
  const firstPreparerDetailsComplete = preparerFields.every((key) =>
    Boolean(declarations[firstParty?.id]?.[key]?.trim?.())
  );
  const applyPreparerDetailsToAll = () => {
    if (!firstParty || !firstPreparerDetailsComplete) return;

    setDeclarations((current) => {
      const source = current[firstParty.id] || {};
      const sharedDetails = Object.fromEntries(
        preparerFields.map((key) => [key, source[key] || ""])
      );
      const next = { ...current };

      parties.forEach((company) => {
        next[company.id] = {
          ...next[company.id],
          ...sharedDetails,
        };
      });

      return next;
    });
    setPreparerShareNotice(
      `Preparer details applied to all ${parties.length} companies.`
    );
  };
  const setAllTransferorDeclarationsConfirmed = (confirmed) => {
    setDeclarations((current) => {
      const next = { ...current };

      transferors.forEach((company) => {
        next[company.id] = {
          ...next[company.id],
          transferorDeclarationsConfirmed: confirmed,
        };
      });

      return next;
    });
  };
  const review = () => {
    w.setShowResults(false);
    w.setStep(2);
  };
  const exportOutput = (type) => {
    setOutputExportError("");
    setOutputExporting(type);
    try {
      const detailedRows = buildDetailedOutputRows(r);
      if (type === "pdf") exportGroupReliefPdf(r, detailedRows);
      else exportGroupReliefExcel(r, detailedRows);
    } catch (error) {
      setOutputExportError(
        error?.message || "Unable to export the Group Relief results."
      );
    } finally {
      setOutputExporting("");
    }
  };
  const metrics = [
    {
      label: "Tax payable before ECI (before Group Relief)",
      value: r.beforeSummary.taxPayableBeforeEci,
      color: "text.primary",
      bgColor: "action.hover",
    },
    {
      label: "Tax payable before ECI (after Group Relief)",
      value: r.afterSummary.taxPayableBeforeEci,
      color: "primary.main",
      bgColor: "primary.50",
    },
    {
      label: "Tax savings before ECI",
      value: r.taxSavingBeforeEci,
      color: "success.main",
      bgColor: "success.50",
    },
  ];
  const detailedOutputRows = buildDetailedOutputRows(r);
  const totalTransferAmount = (r.transfers || []).reduce(
    (total, transfer) => total + numberValue(transfer.totalTransferred),
    0
  );
  const totalClaimAmount = totalTransferAmount;
  return (
    <Stack spacing={3}>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Box>
          <Typography variant='h5' fontWeight={750}>
            Group Relief Results
          </Typography>
          <Typography color='text.secondary'>
            YA {r.settings.yearOfAssessment} · FYE{" "}
            {r.settings.commonFinancialYearEnd} · All amounts in SGD
          </Typography>
        </Box>
        <Button onClick={review}>Edit inputs</Button>
      </Stack>
      {w.resultStale && (
        <Alert
          severity='warning'
          action={<Button onClick={review}>Review inputs</Button>}
        >
          These results are out of date. Confirm the updated inputs and optimise
          again. Form downloads are unavailable.
        </Alert>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {metrics.map(({ label, value, color }, index) => (
          <Box
            key={label}
            sx={(theme) => ({
              flex: 1,
              display: "flex",
              flexDirection: "column",
              px: 2.5,
              py: 2,
              borderLeft: index === 0 ? "none" : 1,
              borderColor: "divider",

              ...(label === "Tax savings before ECI" && {
                bgcolor: alpha(theme.palette.success.main, 0.06),
              }),
            })}
          >
            <Typography
              variant='body2'
              color='text.secondary'
              fontWeight={500}
              sx={{ mb: 0.5 }}
            >
              {label}
            </Typography>

            <Typography
              variant='h5'
              fontWeight={750}
              color={color}
              sx={{ mt: "auto" }}
            >
              {formatAmount(value, false)}
            </Typography>
          </Box>
        ))}
      </Box>
      {r.taxSavingBeforeEci <= 0 && (
        <Alert severity='info'>
          No additional tax savings for these inputs.
          {r.transfers.length
            ? " The allocation contains transfers but does not reduce tax payable before ECI."
            : " No transfers were recommended."}
        </Alert>
      )}
      <Paper
        component='section'
        aria-labelledby='gro-outcome-title'
        variant='outlined'
        sx={{ p: 2, borderRadius: 2 }}
      >
        <Typography variant='subtitle1' fontWeight={700} sx={{ mb: 1 }}>
          Recommended Transfers{" "}
          <JustInfo title='Claims are limited to income remaining after the company uses its own losses and prior-year and current-year donations. Eligible current-year items are then allocated: CA first, trade losses next, and donations last. Every pair must meet the group relationship and continuous-period requirements.' />
        </Typography>
        <TableContainer
          tabIndex={0}
          role='region'
          aria-label='Recommended transfer schedule'
          sx={computationTableContainerSx}
        >
          <Table
            stickyHeader
            size='small'
            aria-label='Recommended transfers'
            sx={{
              ...computationTableSx,
              "& .MuiTableCell-root": {
                ...computationTableSx["& .MuiTableCell-root"],
                minWidth: 140,
              },
              "& tr > :first-child": {
                ...computationTableSx["& tr > :first-child"],
                minWidth: 56,
                maxWidth: 72,
              },
              "& tr > :nth-child(2), & tr > :nth-child(3)": {
                minWidth: 200,
              },
            }}
          >
            <TableHead>
              <TableRow>
                {[
                  "Order",
                  "Transferor",
                  "Claimant",
                  "CA",
                  "Trade losses",
                  "Donations",
                  "Total",
                  "Qualifying days",
                ].map((label, index) => (
                  <TableCell
                    key={label}
                    scope='col'
                    align={index >= 3 ? "right" : "left"}
                    sx={{ fontWeight: 700 }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {r.transfers.map((t, i) => (
                <TableRow key={`${t.transferorId}:${t.claimantId}`}>
                  <TableCell component='th' scope='row'>
                    {i + 1}
                  </TableCell>
                  <TableCell>
                    <ResultCompanyLabel
                      company={companyById.get(t.transferorId)}
                    />
                  </TableCell>
                  <TableCell>
                    <ResultCompanyLabel
                      company={companyById.get(t.claimantId)}
                    />
                  </TableCell>
                  {fields.map(([key]) => (
                    <TableCell key={key} align='right'>
                      {formatAmount(t[key], false)}
                    </TableCell>
                  ))}
                  <TableCell
                    align='right'
                    sx={{ fontWeight: 750, color: "primary.main" }}
                  >
                    {formatAmount(t.totalTransferred, false)}
                  </TableCell>
                  <TableCell align='right'>{t.pairQualifyingDays}</TableCell>
                </TableRow>
              ))}
              {!r.transfers.length && (
                <TableRow>
                  <TableCell colSpan={8}>No transfers recommended.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{
            display: "flex",
            alignItems: "stretch",
            mt: 1,
            border: 1,
            borderColor: "divider",
            borderRadius: 1.5,
            overflow: "hidden",
          }}
        >
          {[
            { label: "Total transfer", value: totalTransferAmount },
            { label: "Total claim", value: totalClaimAmount },
          ].map(({ label, value }, index) => (
            <Box
              key={label}
              sx={{
                flex: 1,
                px: 1.5,
                py: 1,
                borderLeft: index === 0 ? "none" : 1,
                borderColor: "divider",
              }}
            >
              <Typography
                variant='caption'
                color='text.secondary'
                fontWeight={600}
                display='block'
              >
                {label}
              </Typography>
              <Typography variant='body1' fontWeight={750} color='primary.main'>
                SGD {formatAmount(value, false)}
              </Typography>
            </Box>
          ))}
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent='space-between'
          gap={1}
          sx={{
            mt: 2,
            pt: 2,
            mb: 1,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant='subtitle1' fontWeight={700}>
            Detailed Computation Output
          </Typography>
          <Stack direction='row' gap={1} flexWrap='wrap'>
            <Button
              size='small'
              variant='outlined'
              color='error'
              startIcon={<PictureAsPdfOutlinedIcon />}
              disabled={Boolean(outputExporting) || Boolean(w.resultStale)}
              onClick={() => exportOutput("pdf")}
              sx={{
                textTransform: "none",
                color: "error.main",
                borderColor: "error.main",
                "&:hover": {
                  color: "error.light",
                  borderColor: "error.light",
                  bgcolor: alpha("#f44336", 0.04),
                },
              }}
            >
              {outputExporting === "pdf" ? "Preparing PDF…" : "Export PDF"}
            </Button>
            <Button
              size='small'
              variant='outlined'
              color='success'
              startIcon={<TableViewOutlinedIcon />}
              disabled={Boolean(outputExporting) || Boolean(w.resultStale)}
              onClick={() => exportOutput("excel")}
              sx={{
                textTransform: "none",
                color: "success.main",
                borderColor: "success.main",
                "&:hover": {
                  color: "success.light",
                  borderColor: "success.light",
                  bgcolor: alpha("#4caf50", 0.04),
                },
              }}
            >
              {outputExporting === "excel"
                ? "Preparing Excel…"
                : "Export Excel"}
            </Button>
          </Stack>
        </Stack>

        {outputExportError && (
          <Alert severity='error' sx={{ mb: 1.5 }}>
            {outputExportError}
          </Alert>
        )}

        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ mb: 1, lineHeight: 1.6 }}
        >
          Comparison for the current YA. Group Relief transfers do not affect
          the transferor's own tax position. Tax relating to preceding periods
          is excluded from the case totals.
        </Typography>
        <TableContainer
          tabIndex={0}
          role='region'
          aria-label='Company tax comparison'
          sx={{ ...computationTableContainerSx, maxHeight: "none" }}
        >
          <Table
            stickyHeader
            size='small'
            aria-label='Optimised computation output'
            sx={computationTableSx}
          >
            <TableHead>
              <TableRow>
                <TableCell scope='col' sx={{ fontWeight: 700 }}>
                  Description
                </TableCell>
                {r.companies.map((c, index) => (
                  <TableCell
                    key={c.id}
                    scope='col'
                    align='right'
                    sx={{ minWidth: 160, maxWidth: 200 }}
                  >
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      display='block'
                    >
                      Company {index + 1}
                    </Typography>
                    <ResultCompanyLabel company={c} justifyContent='flex-end' />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {detailedOutputRows.map((row) => (
                <TableRow
                  key={row.id}
                  sx={(theme) => ({
                    ...(row.className === "transfer-step-row" && {
                      "& > .MuiTableCell-root": {
                        color: "secondary.dark",
                        fontWeight: 650,
                        bgcolor: alpha(theme.palette.secondary.main, 0.08),
                      },
                      "& > :first-child": {
                        bgcolor: alpha(theme.palette.secondary.main, 0.08),
                        borderRight: "2px solid",
                        borderRightColor: "secondary.main",
                      },
                    }),
                    ...(row.className === "input-balance-row" && {
                      "& > :first-child": { color: "text.secondary" },
                    }),
                    ...(row.id === "other-income" && {
                      "& > .MuiTableCell-root": { color: "text.secondary" },
                    }),
                    ...(row.className === "subtotal-row" && {
                      "& > .MuiTableCell-root": {
                        fontWeight: 750,
                        borderTop: "1px solid",
                        borderTopColor: "divider",
                      },
                    }),
                    ...(row.className === "spacer-row" && {
                      height: 12,
                      "& > .MuiTableCell-root": {
                        py: 0.5,
                        borderBottom: 0,
                        bgcolor: "background.default",
                      },
                    }),
                    ...(row.id === "tax-payable-before-eci" && {
                      "& > .MuiTableCell-root": {
                        fontWeight: 750,
                        color: "primary.main",
                        borderTop: "1px solid",
                        borderTopColor: "divider",
                        py: 1.5,
                      },
                      "& > :first-child": {
                        borderLeft: "3px solid",
                        borderLeftColor: "primary.main",
                      },
                    }),
                  })}
                >
                  <TableCell
                    component='th'
                    scope='row'
                    sx={(theme) =>
                      row.className === "spacer-row"
                        ? { bgcolor: "background.default" }
                        : {
                            bgcolor:
                              row.className === "transfer-step-row"
                                ? alpha(theme.palette.secondary.main, 0.08)
                                : "action.hover",
                            fontWeight: 650,
                            borderRight: "2px solid",
                            borderRightColor:
                              row.className === "transfer-step-row"
                                ? "secondary.main"
                                : "divider",
                          }
                    }
                  >
                    {row.label}
                  </TableCell>
                  {r.companies.map((c, index) => (
                    <TableCell
                      key={c.id}
                      align='right'
                      sx={row.isSubtotal ? { fontWeight: 750 } : undefined}
                    >
                      {formatDetailedOutputCell(row.values[index], row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {r.companies.some((c) => c.taxPeriodType === "cross_ya") && (
        <Paper variant='outlined' sx={{ p: 2 }}>
          <Typography fontWeight={700}>Preceding YA — unchanged</Typography>
          {r.companies.map((c, i) => {
            const p = r.preComputations[i].resolution.periods.find(
              (period) => period.key === "preceding"
            );
            return p ? (
              <Stack
                key={c.id}
                direction='row'
                alignItems='center'
                gap={0.75}
                flexWrap='wrap'
              >
                <ResultCompanyLabel company={c} />
                <Typography component='span'>
                  · {p.label} Tax payable before ECI:{" "}
                  {formatAmount(p.taxPayableBeforeEci, false)}
                </Typography>
              </Stack>
            ) : null;
          })}
        </Paper>
      )}
      <Paper
        component='section'
        aria-labelledby='gro-forms-title'
        variant='outlined'
        sx={(theme) => ({
          p: { xs: 2, sm: 2.5 },
          borderRadius: 2,
          border: "2px solid",
          borderColor: "primary.main",
          borderLeftWidth: 6,
          backgroundImage: `linear-gradient(${alpha(theme.palette.primary.main, 0.035)}, ${alpha(theme.palette.primary.main, 0.035)})`,
        })}
      >
        {/* Header */}
        <Typography id='gro-forms-title' variant='h6' fontWeight={700}>
          Group Relief Forms — Preparer Details
        </Typography>

        <Typography
          id='gro-company-details-help'
          variant='body1'
          color='text.secondary'
          sx={{ mt: 0.5, mb: 2.5, lineHeight: 1.6 }}
        >
          Enter the preparer details for each company. If the same person
          prepared every form, complete the first company and select “Apply
          preparer details to all”. Transfer amounts and qualifying dates are
          filled in automatically.
        </Typography>

        {!r.transfers.length ? (
          <Alert severity='info'>
            There are no Group Relief transfers available for GR-A or GR-B.
          </Alert>
        ) : (
          <>
            <Stack
              spacing={2}
              role='region'
              aria-label='Company preparer details'
              aria-describedby='gro-company-details-help'
              tabIndex={parties.length > 2 ? 0 : undefined}
              sx={{
                maxHeight: parties.length > 2 ? "min(580px, 62vh)" : "none",
                overflowY: parties.length > 2 ? "auto" : "visible",
                pr: parties.length > 2 ? 1 : 0,
                scrollbarGutter: parties.length > 2 ? "stable" : "auto",
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: 2,
                  borderRadius: 1,
                },
              }}
            >
              {parties.map((c) => {
                return (
                  <Paper
                    key={c.id}
                    variant='outlined'
                    sx={{
                      p: 2,
                      borderRadius: 2,
                    }}
                  >
                    {/* Company */}
                    <Box sx={{ mb: 1.5 }}>
                      <ResultCompanyLabel company={c} />

                      <Typography variant='caption' color='text.secondary'>
                        {c.uen}
                      </Typography>
                    </Box>

                    {/* Preparer fields */}
                    <Grid container spacing={1.5}>
                      {[
                        ["fullName", "Preparer full name"],
                        ["capacity", "Capacity"],
                        ["contactNumber", "Contact number"],
                        ["date", "Date"],
                      ].map(([key, label]) => (
                        <Grid item xs={12} sm={6} md={3} key={key}>
                          {key === "date" ? (
                            <CustomDatePicker
                              id={`gro-form-date-${c.id}`}
                              fullWidth
                              size='small'
                              label={label}
                              disabled={w.resultStale || Boolean(w.exporting)}
                              selected={
                                declarations[c.id]?.date
                                  ? parseISO(declarations[c.id].date)
                                  : null
                              }
                              onChange={(date) => {
                                setPreparerShareNotice("");
                                setDeclarations((prev) => ({
                                  ...prev,
                                  [c.id]: {
                                    ...prev[c.id],
                                    date: date
                                      ? format(date, "yyyy-MM-dd")
                                      : "",
                                  },
                                }));
                              }}
                            />
                          ) : (
                            <TextField
                              fullWidth
                              size='small'
                              label={label}
                              disabled={w.resultStale || Boolean(w.exporting)}
                              value={declarations[c.id]?.[key] || ""}
                              onChange={(event) => {
                                setPreparerShareNotice("");
                                setDeclarations((prev) => ({
                                  ...prev,
                                  [c.id]: {
                                    ...prev[c.id],
                                    [key]: event.target.value,
                                  },
                                }));
                              }}
                            />
                          )}
                        </Grid>
                      ))}
                    </Grid>

                    {c.id === firstParty?.id && parties.length > 1 && (
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        alignItems={{ xs: "stretch", sm: "center" }}
                        gap={1}
                        sx={{ mt: 1.5 }}
                      >
                        <Button
                          variant='outlined'
                          startIcon={<ContentCopyOutlinedIcon />}
                          disabled={
                            w.resultStale ||
                            Boolean(w.exporting) ||
                            !firstPreparerDetailsComplete
                          }
                          onClick={applyPreparerDetailsToAll}
                          sx={{
                            alignSelf: { xs: "stretch", sm: "flex-start" },
                            textTransform: "none",
                          }}
                        >
                          Apply preparer details to all
                        </Button>
                        <Typography variant='caption' color='text.secondary'>
                          Fill all four fields above to share them with every
                          company.
                        </Typography>
                      </Stack>
                    )}

                    {c.id === firstParty?.id && preparerShareNotice && (
                      <Alert severity='success' role='status' sx={{ mt: 1.5 }}>
                        {preparerShareNotice}
                      </Alert>
                    )}
                  </Paper>
                );
              })}
            </Stack>

            <Box
              sx={{
                mt: 3,
                pt: 2.5,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant='h6' fontWeight={700}>
                Confirm and download
              </Typography>

              <Typography
                variant='body1'
                color='text.secondary'
                sx={{ mt: 0.5, mb: 1.5, lineHeight: 1.6 }}
              >
                Download the forms and obtain the required signatures.
                <JustInfo title='Confirm that each transferor meets the GR-A requirements and that no restricted losses or capital allowances are being transferred.' />
              </Typography>

              <FormControlLabel
                sx={{
                  m: 0,
                  mb: 2,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  "&:hover": { bgcolor: "action.hover" },
                }}
                control={
                  <Checkbox
                    checked={allTransferorDeclarationsConfirmed}
                    disabled={w.resultStale || Boolean(w.exporting)}
                    onChange={(event) =>
                      setAllTransferorDeclarationsConfirmed(
                        event.target.checked
                      )
                    }
                  />
                }
                label={
                  <Typography variant='body1' fontWeight={600}>
                    I confirm all GR-A transferor declarations have been
                    reviewed.
                  </Typography>
                }
              />

              {w.exportError && (
                <Alert severity='error' sx={{ mb: 2 }}>
                  {w.exportError}
                </Alert>
              )}

              <Stack
                direction={{ xs: "column", sm: "row" }}
                gap={1}
                flexWrap='wrap'
              >
                <Button
                  variant='contained'
                  size='large'
                  sx={{ minWidth: { sm: 180 } }}
                  disabled={
                    w.resultStale || !r.transfers.length || Boolean(w.exporting)
                  }
                  onClick={() => w.exportForm("gra", declarations)}
                >
                  {w.exporting === "gra" ? "Preparing PDF…" : "Download GR-A"}
                </Button>

                <Button
                  variant='outlined'
                  size='large'
                  sx={{ minWidth: { sm: 180 } }}
                  disabled={
                    w.resultStale || !r.transfers.length || Boolean(w.exporting)
                  }
                  onClick={() => w.exportForm("grb", declarations)}
                >
                  {w.exporting === "grb" ? "Preparing PDF…" : "Download GR-B"}
                </Button>
              </Stack>
            </Box>
          </>
        )}
      </Paper>
    </Stack>
  );
}
